import React, { useCallback, useState, useEffect, Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import "./App.css";
import ToastContainer from "./components/Toast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ROLES } from "./lib/roles";
import { withNext } from "./lib/redirects";
import { DEFAULT_CURRENCY } from "./config/payments";
import { NotificationProvider } from "./contexts/NotificationContext";
import { RankingProvider } from "./contexts/RankingContext";
import { CreditProvider } from "./contexts/CreditContext";
import { CookieConsentProvider } from "./contexts/CookieConsentContext";
import RoleRoute from "./components/RoleRoute";
import RootLayout from "./components/layouts/RootLayout";
import CookieConsentBanner from "./components/CookieConsentBanner";
import Logo from "./components/Logo";

// Eager loaded components for initial paint logic
import LoginNew from "./pages/LoginNew";

// The public marketing site (talentfora.in) now owns the landing page. The
// product app's own Home page has been removed, so "/" redirects out to the
// marketing site. Override with REACT_APP_MARKETING_URL per environment.
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || "https://talentfora.in";

// Lazy loaded components for code splitting
const UploadPageNew = lazy(() => import("./pages/UploadPageNew"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const SessionDetailPage = lazy(() => import("./pages/SessionDetailPage"));
const SessionsPage = lazy(() => import("./pages/SessionsPage"));
const ResumeLibraryPage = lazy(() => import("./pages/ResumeLibraryPage"));
const MailboxConnectionsPage = lazy(() => import("./pages/MailboxConnectionsPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const AcceptInvitePage = lazy(() => import("./pages/AcceptInvitePage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const CreditPackagesPage = lazy(() => import("./pages/CreditPackagesPage"));
const About = lazy(() => import("./pages/About"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const SubscribeCheckoutPage = lazy(() => import("./pages/SubscribeCheckoutPage"));
const SubscriptionManagePage = lazy(() => import("./pages/SubscriptionManagePage"));
const CreditWalletPage = lazy(() => import("./pages/CreditWalletPage"));
const BillingHistoryPage = lazy(() => import("./pages/BillingHistoryPage"));
const Forbidden = lazy(() => import("./pages/Forbidden"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const GlobalAdminDashboard = lazy(() => import("./pages/GlobalAdminDashboard"));

// Legal / policy pages (publicly accessible — required for Razorpay activation + DPDPA/GDPR compliance)
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const ShippingPolicyPage = lazy(() => import("./pages/ShippingPolicyPage"));
const ContactUsPage = lazy(() => import("./pages/ContactUsPage"));
const CancellationRefundPage = lazy(() => import("./pages/CancellationRefundPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const AIOversightPage = lazy(() => import("./pages/AIOversightPage"));
const SubProcessorsPage = lazy(() => import("./pages/SubProcessorsPage"));


// Reset scroll to the top on every route change. Without this, React Router v6
// preserves the previous scroll position, so navigating to a (often shorter)
// page lands the user mid/bottom of it. Skip when a hash is present so in-page
// anchor navigation (e.g. /#book-demo via useHashScroll) still works.
function ScrollToTopOnRouteChange() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}


// The app's boot screen: shown while the auth check is in flight, while a lazy
// route chunk loads, and while an entry route works out where to send someone.
const BootSplash = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#FBFBF9' }}>
    <Logo size={52} variant="rust" />
    <div style={{
      width: '36px', height: '36px',
      border: '3px solid #E6E5E1',
      borderTopColor: '#1F7D91',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
  </div>
);


// ProtectedRoute reads from AuthContext (same source as RoleRoute) to avoid race conditions
const ProtectedRoute = ({ children }) => {
  const { authenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <BootSplash />;
  }

  if (!authenticated) {
    // Keep the page they were reaching for, so signing in lands there instead of
    // on the dashboard — the difference between finishing a plan checkout and
    // having to hunt for it again.
    return <Navigate to={withNext("/loginNew", `${location.pathname}${location.search}`)} replace />;
  }

  return children;
};


// GuestRoute is the mirror of ProtectedRoute: it keeps already-authenticated
// users out of the auth pages (login, register, password reset). Without it a
// logged-in user could re-open /loginNew or /register and re-submit those flows.
const GuestRoute = ({ children }) => {
  const { authenticated, loading, user } = useAuth();

  if (loading) {
    return <BootSplash />;
  }

  if (authenticated) {
    return <Navigate to={user?.role === ROLES.GLOBAL_ADMIN ? "/global-admin" : "/dashboard"} replace />;
  }

  return children;
};


// MarketingRedirect sends the app root ("/") out to the public marketing site.
// It is safe against the MSAL auth callback: index.js processes the redirect
// response and rewrites "/" -> "/loginNew" *before* React renders, so this
// component only mounts for genuine visits to the bare product root.
const MarketingRedirect = () => {
  useEffect(() => {
    window.location.replace(MARKETING_URL);
  }, []);

  return <BootSplash />;
};


// StartPlanRedirect ("/start/:planId") is where the marketing site's plan buttons
// land. talentfora.in is statically rendered, so it can't tell a signed-in visitor
// from a guest; it hands the chosen plan over and this resolves the destination the
// moment AuthContext settles — that plan's checkout when signed in, registration
// (with the checkout carried as ?next=) when not.
//
// Deliberately NOT pointed at /pricing: that made the visitor watch a second
// pricing page load and then get moved along, which reads as a misfired link. This
// route renders only the boot splash, so the one unavoidable wait — the /auth/status
// round-trip, which no static page can skip — looks like loading, not a wrong turn.
const StartPlanRedirect = () => {
  const { planId } = useParams();
  const [searchParams] = useSearchParams();
  const { authenticated, loading } = useAuth();

  if (loading) {
    return <BootSplash />;
  }

  // Only a well-formed currency code is passed through; anything else falls back to
  // the checkout page's own default rather than riding along in the URL.
  const requested = (searchParams.get('currency') || '').toUpperCase();
  const currency = /^[A-Z]{3}$/.test(requested) ? requested : DEFAULT_CURRENCY;
  const checkout = `/subscribe/${encodeURIComponent(planId)}?currency=${currency}`;

  return <Navigate to={authenticated ? checkout : withNext('/register', checkout)} replace />;
};


function App() {
  const [toasts, setToasts] = useState([]);
  const pushToast = useCallback((t) => setToasts((prev) => [...prev, { id: Date.now(), ...t }]), []);
  const closeToast = useCallback((id) => setToasts((prev) => prev.filter((x) => x.id !== id)), []);

  useEffect(() => {
    const handleNotificationToast = (e) => {
      const n = e.detail;
      pushToast({
        title: n.title,
        message: n.message,
        type: n.type
      });
    };
    window.addEventListener('show-notification-toast', handleNotificationToast);
    return () => window.removeEventListener('show-notification-toast', handleNotificationToast);
  }, [pushToast]);

  return (
    <AuthProvider>
      <NotificationProvider>
        <CreditProvider>
          <RankingProvider>
            <CookieConsentProvider>
          <ToastContainer toasts={toasts} onClose={closeToast} />
          <CookieConsentBanner />
          <Suspense fallback={<BootSplash />}>
            <ScrollToTopOnRouteChange />
            <Routes>
              {/* Public Routes */}
              {/* Root now belongs to the public marketing site (talentfora.in). */}
              <Route path="/" element={<MarketingRedirect />} />
              {/* Auth pages — guests only; logged-in users are bounced to their dashboard */}
              <Route path="/loginNew" element={<GuestRoute><LoginNew /></GuestRoute>} />
              <Route path="/register" element={<GuestRoute><Register /></GuestRoute>} />
              <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
              <Route path="/reset-password/:token" element={<GuestRoute><ResetPassword /></GuestRoute>} />
              <Route path="/verify-email/:token" element={<VerifyEmail />} />
              {/* Deliberately NOT wrapped in GuestRoute: that bounces authenticated
                  users to /dashboard, which would silently swallow an invite link for
                  anyone already signed in. The page handles that case itself. */}
              <Route path="/invite/:token" element={<AcceptInvitePage />} />
              <Route path="/pricing" element={<PricingPage pushToast={pushToast} />} />
              {/* Entry point for a plan chosen on the marketing site — resolves to
                  checkout or registration without rendering a page of its own. */}
              <Route path="/start/:planId" element={<StartPlanRedirect />} />
              <Route path="/credit-packages" element={<CreditPackagesPage pushToast={pushToast} />} />
              <Route path="/about" element={<About />} />
              <Route path="/forbidden" element={<Forbidden />} />

              {/* Legal / policy pages (public, indexable) */}
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/shipping" element={<ShippingPolicyPage />} />
              <Route path="/contact" element={<ContactUsPage />} />
              <Route path="/cancellation-refund" element={<CancellationRefundPage />} />
              <Route path="/cookies" element={<CookiePolicyPage />} />
              <Route path="/ai-oversight" element={<AIOversightPage />} />
              <Route path="/sub-processors" element={<SubProcessorsPage />} />

              {/* Protected Routes - Use new RootLayout */}
              <Route element={
                <ProtectedRoute>
                  <RootLayout pushToast={pushToast} />
                </ProtectedRoute>
              }>
                <Route path="/dashboard" element={<Dashboard pushToast={pushToast} />} />
                <Route path="/sessions/:sessionId" element={<SessionDetailPage pushToast={pushToast} />} />
                <Route path="/upload" element={<UploadPageNew pushToast={pushToast} />} />
                <Route path="/sessions" element={<SessionsPage pushToast={pushToast} />} />
                <Route path="/library" element={<ResumeLibraryPage pushToast={pushToast} />} />
                <Route path="/mailbox" element={<MailboxConnectionsPage pushToast={pushToast} />} />
                <Route path="/wallet" element={<CreditWalletPage pushToast={pushToast} />} />
                <Route path="/billing" element={<BillingHistoryPage pushToast={pushToast} />} />
                <Route path="/profile" element={<ProfilePage pushToast={pushToast} />} />
                <Route path="/settings" element={<SettingsPage pushToast={pushToast} />} />
                <Route path="/checkout/:packageId" element={<CheckoutPage pushToast={pushToast} />} />
                <Route path="/subscribe/:planId" element={<SubscribeCheckoutPage pushToast={pushToast} />} />
                <Route path="/subscription" element={<SubscriptionManagePage pushToast={pushToast} />} />

                {/* Admin Route */}
                <Route
                  path="/admin"
                  element={
                    <RoleRoute role={ROLES.ADMIN}>
                      <AdminDashboard pushToast={pushToast} />
                    </RoleRoute>
                  }
                />

                {/* Global Admin Route */}
                <Route
                  path="/global-admin"
                  element={
                    <RoleRoute role={ROLES.GLOBAL_ADMIN}>
                      <GlobalAdminDashboard pushToast={pushToast} />
                    </RoleRoute>
                  }
                />
              </Route>

              {/* Catch-all: unknown client-side routes render the branded 404.
                  Real HTTP 404 for non-existent paths is enforced at the edge
                  by Azure SWA (public/404.html + responseOverrides). */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
            </CookieConsentProvider>
          </RankingProvider>
        </CreditProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
